'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'

import { useGetTypeActualites, useGetActualites } from '@/actions/actualite'
import type { IActualite, ITypeActualite } from '@/types/actualite'
import Button from '../ui/Button'
import { NewsCard, NewsCardSkeleton } from './NewsCard'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ALL_TAB_ID = 'all'

// ---------------------------------------------------------------------------
// TabButton
// ---------------------------------------------------------------------------

interface TabButtonProps {
  label: string
  isActive: boolean
  onClick: () => void
}

const TabButton: React.FC<TabButtonProps> = ({ label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`rounded-3xl px-4 sm:px-6 h-6.5 sm:h-11.5 cursor-pointer text-[8px] sm:text-[12px] leading-3.5 font-bold transition border border-[#EFEFEF] ${
      isActive
        ? 'bg-[#1B365E] text-white'
        : 'bg-white text-[#1B365E] hover:bg-[#1B365E]/10'
    }`}
  >
    {label}
  </button>
)

// ---------------------------------------------------------------------------
// NewsSection — renders a type's news in grid + mobile swiper
// ---------------------------------------------------------------------------

interface NewsSectionProps {
  type: ITypeActualite
  items: IActualite[]
  loading: boolean
}

const NewsSection: React.FC<NewsSectionProps> = ({ type, items, loading }) => (
  <div className="my-10 lg:my-20 first:mt-0">
    <div className="flex flex-col lg:flex-row gap-4 lg:items-end justify-between">
      <h2 className="text-[#1B365E] text-[30px] md:text-[40px] font-red-hat font-light leading-10 md:leading-13.25 tracking-[0.02rem]">
        {`L'actualité sur les formations en ${type.name}`}
      </h2>
      <Link href={`/actualites/${type.slug}`}>
        <Button variant="outline-gradient" className="h-9! text-[15px]! text-nowrap">
          Voir nos actualités en {type.name}
        </Button>
      </Link>
    </div>

    {loading ? (
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <NewsCardSkeleton />
        <NewsCardSkeleton />
        <NewsCardSkeleton />
      </div>
    ) : items.length === 0 ? (
      <p className="text-[#676767] text-sm mt-8">
        Aucune actualité disponible pour cette catégorie.
      </p>
    ) : (
      <>
        {/* Desktop grid */}
        <div className="mt-8 hidden lg:grid grid-cols-1 lg:grid-cols-3 gap-10">
          {items.map((item) => (
            <NewsCard
              key={item.id}
              item={item}
              href={`/actualites/${type.slug}/${item.slug}`}
            />
          ))}
        </div>

        {/* Mobile carousel */}
        <div className="lg:hidden mt-6">
          <Swiper
            modules={[Pagination]}
            spaceBetween={16}
            slidesPerView={1}
            pagination={{
              clickable: true,
              el: `.custom-pagination-${type.slug}`,
            }}
            className="pb-0"
          >
            {items.map((item) => (
              <SwiperSlide key={`${type.slug}-${item.id}`} className="pb-0">
                <NewsCard item={item} href={`/actualites/${type.slug}/${item.slug}`} />
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            className={`custom-pagination-${type.slug} swiper-pagination flex justify-center gap-2 mt-4`}
            style={{ position: 'static', bottom: 'auto', top: 'auto' }}
          />
        </div>
      </>
    )}
  </div>
)

// ---------------------------------------------------------------------------
// NewsSectionWithFetch — fetches its own data for a given type.
//
// Placing the hook inside a child component is the standard React pattern
// for calling hooks inside a list: each section independently manages its
// own SWR cache entry, so switching back to the "All" tab reuses cached data
// without extra network round-trips.
// ---------------------------------------------------------------------------

interface NewsSectionWithFetchProps {
  type: ITypeActualite
}

const NewsSectionWithFetch: React.FC<NewsSectionWithFetchProps> = ({ type }) => {
  const { actualites, actualitesLoading } = useGetActualites(type.slug)

  return <NewsSection type={type} items={actualites.slice(0, 3)} loading={actualitesLoading} />
}

// ---------------------------------------------------------------------------
// TabBarSkeleton
// ---------------------------------------------------------------------------

const TabBarSkeleton: React.FC = () => (
  <div className="flex flex-wrap justify-center gap-[15.25px] sm:gap-10 mb-9.25">
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className="h-6.5 sm:h-11.5 w-28 sm:w-36 bg-gray-200 animate-pulse rounded-3xl"
      />
    ))}
  </div>
)

// ---------------------------------------------------------------------------
// AllTheNews — main orchestrator
// ---------------------------------------------------------------------------

const AllTheNews: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB_ID)

  const { typeActualites, typeActualitesLoading, typeActualitesError } =
    useGetTypeActualites()

  const activeType = typeActualites.find((t) => t.slug === activeTab)

  if (typeActualitesError) {
    return (
      <section className="px-4 pt-10 md:pt-14.25 sm:pb-6.25">
        <div className="max-w-344 mx-auto w-full text-center py-16 text-red-500">
          Une erreur est survenue lors du chargement des categories. Veuillez reessayer.
        </div>
      </section>
    )
  }

  return (
    <section className="px-4 pt-10 md:pt-14.25 sm:pb-6.25">
      <div className="max-w-344 mx-auto w-full">

        {/* ---- Tab bar ---- */}
        {typeActualitesLoading ? (
          <TabBarSkeleton />
        ) : (
          <div className="flex flex-wrap justify-center gap-[15.25px] sm:gap-10 mb-9.25">
            <TabButton
              label="Toutes les actualites"
              isActive={activeTab === ALL_TAB_ID}
              onClick={() => setActiveTab(ALL_TAB_ID)}
            />
            {typeActualites.map((type) => (
              <TabButton
                key={type.id}
                label={type.name}
                isActive={activeTab === type.slug}
                onClick={() => setActiveTab(type.slug)}
              />
            ))}
          </div>
        )}

        {/* ---- News content ---- */}
        {typeActualites.length === 0 && !typeActualitesLoading ? (
          <p className="text-center text-[#676767] py-16">
            Aucune cat&eacute;gorie d&apos;actualit&eacute; disponible.
          </p>
        ) : activeTab === ALL_TAB_ID ? (
          typeActualites.map((type) => (
            <NewsSectionWithFetch key={type.id} type={type} />
          ))
        ) : activeType ? (
          <NewsSectionWithFetch type={activeType} />
        ) : null}

      </div>
    </section>
  )
}

export default AllTheNews
